/**
 * `storeAppearances.*` — the named-appearance library (spec 50).
 *
 * The list screen's whole surface. Two things about the shapes here are
 * deliberate:
 *
 *  - **No `isActive` is ever accepted on input.** Activity is not a property a
 *    caller sets; it is derived server-side from
 *    `stores.published_home_version_id` (spec 50 R2). It appears on output only.
 *  - **Activate takes an appearance id, not a version id.** Publishing a
 *    specific version stays `storeHome.publish` — the rollback path. Activating
 *    means "put this design's latest saved work live", which is the same pointer
 *    move phrased for a different screen (R3).
 */
import { z } from "zod";
import { uuid } from "../_common.js";

/**
 * 1–60 characters after trim (R6). Trimmed here so " Festive " and "Festive"
 * cannot both exist — the uniqueness index is on `lower(name)`, which would not
 * catch a whitespace difference.
 */
export const appearanceName = z
  .string()
  .trim()
  .min(1, { message: "Give this appearance a name of 1–60 characters." })
  .max(60, { message: "Give this appearance a name of 1–60 characters." });

/** One row in the library list. */
export const appearanceSummary = z.object({
  id: uuid,
  name: z.string(),
  /** Which template seeded it; null for the backfilled and the duplicated. */
  templateKey: z.string().nullable(),
  /**
   * Derived, never stored: this appearance owns `published_home_version_id`.
   * Exactly one row in the list can carry `true` (R2).
   */
  isActive: z.boolean(),
  /** Sections on the current version — the "8 sections" line on the card. */
  sectionCount: z.number().int(),
  /**
   * The current version's composition, for the card's preview thumbnail. Sent
   * with the list so the gallery doesn't fire one request per card; the list is
   * capped at a handful of rows per store in practice.
   */
  sections: z.array(z.unknown()),
  theme: z.unknown(),
  /** ISO timestamp of the newest save. */
  updatedAt: z.string(),
});
export type AppearanceSummary = z.infer<typeof appearanceSummary>;

export const listAppearancesInput = z.object({});

export const listAppearancesOutput = z.object({
  /** Non-archived only, newest first. Empty → the client shows the gallery (R10). */
  items: z.array(appearanceSummary),
  /** Storefront identity for the "View" link on the active card. */
  storeSlug: z.string(),
  storeName: z.string(),
});

export const createAppearanceInput = z.object({
  /** A key from `STORE_APPEARANCE_TEMPLATES`; unknown keys are refused. */
  templateKey: z.string().trim().min(1).max(32),
  /** Omitted → the template's own name, de-duplicated against the store (R6). */
  name: appearanceName.optional(),
});

export const createAppearanceOutput = z.object({
  appearanceId: uuid,
  versionId: uuid,
  /** The name it actually got — the template's, suffixed if that was taken. */
  name: z.string(),
  /**
   * Whether this create also put it in front of buyers. True only when the
   * store had nothing live (spec 50 R10): building a second appearance from a
   * template must not silently replace the active storefront.
   */
  activated: z.boolean(),
});

export const activateAppearanceInput = z.object({ appearanceId: uuid });

export const activateAppearanceOutput = z.object({
  appearanceId: uuid,
  /** The version now live — `publishHomeVersion`'s return, surfaced. */
  versionId: uuid,
  /** Null when this is the store's first publish. */
  previousVersionId: uuid.nullable(),
});

export const renameAppearanceInput = z.object({
  appearanceId: uuid,
  name: appearanceName,
});

export const renameAppearanceOutput = z.object({ ok: z.literal(true) });

export const duplicateAppearanceInput = z.object({ appearanceId: uuid });

export const duplicateAppearanceOutput = z.object({
  appearanceId: uuid,
  versionId: uuid,
  /** The de-duplicated name the copy actually got, e.g. "Festive (2)". */
  name: z.string(),
});

export const archiveAppearanceInput = z.object({ appearanceId: uuid });

export const archiveAppearanceOutput = z.object({ ok: z.literal(true) });
